/** Clean AI/clipboard paste for plain-text capture fields (single-line). */
export function normalizeCaptureText(raw) {
  if (!raw) return "";
  if (isTerminalPaste(raw)) return normalizeTerminalPaste(raw);
  let text = baseClean(raw);

  return text
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const ANSI_RE =
  // eslint-disable-next-line no-control-regex -- strip terminal color codes from CLI paste
  /\x1b\[[0-9;]*[a-zA-Z]|\x1b\]8;;.*?\x1b\\|\x1b\][^\x07]*\x07|\x1b\][^\x1b]*\x1b\\/g;

const BOX_DRAW_RE = /[┌┐└┘├┤┬┴┼─│╭╮╰╯╞╡═║]/;
const CLI_PROMPT_RE =
  /^PS [A-Z]:\\|^PS>|^[A-Z]:\\[^>]*>|^>\s|^\$\s|^C:\\Users\\|^npm |^yarn |^pnpm |^git |^error |^warning |^ERR!|^\s*at /im;

function baseClean(raw) {
  let text = String(raw)
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\r\n/g, "\n")
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/[\u201C\u201D\u2033]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u00A0/g, " ")
    .replace(ANSI_RE, "");

  if (/<[a-z][\s\S]*>/i.test(text)) {
    text = text
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"');
  }

  return text;
}

/** Web LLM output — headings, lists, bold, markdown tables (not CLI). */
export function hasMarkdownProseSignals(text) {
  if (!text) return false;
  return (
    /^#{1,6}\s+\S/m.test(text) ||
    /^\s*[-*+]\s+(\[[ xX]\]\s+)?\S/m.test(text) ||
    /^\s*\d+\.\s+\S/m.test(text) ||
    /\*\*[^*]+\*\*/.test(text) ||
    /^>\s+\S/m.test(text) ||
    (/^\s*\|.+\|/m.test(text) && /^\s*\|?\s*:?-{3,}:?\s*\|/m.test(text))
  );
}

export function isWebModelPaste(text) {
  if (!text || !text.includes("\n")) return false;
  const t = baseClean(text);
  if (isTerminalPaste(t)) return false;
  return hasMarkdownProseSignals(t);
}

export function isCodeSnippetPaste(text) {
  if (!text) return false;
  const t = baseClean(text).trim();
  if (!t || isTerminalPaste(t)) return false;
  if (/^```[\s\S]+```$/m.test(t)) return true;
  if (hasMarkdownProseSignals(t) && !/^```/m.test(t)) return false;
  const oneLiner = !t.includes("\n");
  if (oneLiner && /^(import |export |const |let |function |class |def |interface |type |#include|package |using )/.test(t)) {
    return true;
  }
  return looksLikeCode(t);
}

export function detectCodeLang(text) {
  const t = String(text || "");
  if (/import React|from ['"]react|<\w+[\s/>]/.test(t)) return "tsx";
  if (/interface \w+|type \w+ =/.test(t)) return "typescript";
  if (/^\s*#!/m.test(t) && /bash|sh/.test(t)) return "bash";
  if (/def \w+|import \w+$|print\(|__init__/.test(t)) return "python";
  if (/^\s*<\?php|namespace \w+;/.test(t)) return "php";
  if (/package main|func \w+\(/.test(t)) return "go";
  if (/^\s*#include|int main\(/.test(t)) return "cpp";
  if (/SELECT .+ FROM|INSERT INTO/i.test(t)) return "sql";
  if (/npm |yarn |pnpm |git |docker /.test(t)) return "bash";
  if (/import .+ from|export (default )?|const \w+ = \(|=> \{/.test(t)) return "javascript";
  return "text";
}

export function normalizeCodePaste(raw) {
  if (!raw) return "";
  let text = baseClean(raw).trim();
  if (/^```/.test(text)) return text;
  const lang = detectCodeLang(text);
  return `\`\`\`${lang}\n${text}\n\`\`\``;
}

/** Terminal / CLI table output — preserve monospace columns. */
export function isTerminalPaste(text) {
  if (!text || typeof text !== "string") return false;
  const raw = text.trim();
  if (!raw.includes("\n")) return false;
  const cleaned = baseClean(raw);
  if (hasMarkdownProseSignals(cleaned)) return false;
  if (ANSI_RE.test(raw)) return true;
  const t = cleaned;
  if (BOX_DRAW_RE.test(t)) return true;
  if (CLI_PROMPT_RE.test(t)) return true;

  const lines = t.split("\n").filter((l) => l.trim());
  if (lines.length < 2) return false;

  const columnar = lines.filter((l) => /\S\s{2,}\S/.test(l)).length;
  if (columnar >= 2 && columnar >= lines.length * 0.35) return true;

  const pipeRows = lines.filter((l) => /^\s*\|?.+\|.+\|?\s*$/.test(l) && (l.match(/\|/g) || []).length >= 2).length;
  if (pipeRows >= 2 && !/^\s*\|?\s*:?-{3,}/m.test(t)) return true;

  return false;
}

/** Pad space-separated CLI columns to a fixed character grid. */
export function alignTerminalColumns(lines) {
  const rows = lines.map((l) => l.replace(/\t/g, "    ").trimEnd());
  const parsed = rows.map((row) => {
    const trimmed = row.trim();
    if (!trimmed) return { kind: "blank", text: "" };
    if (BOX_DRAW_RE.test(row) || /^[-=_.*#\s]+$/.test(trimmed)) return { kind: "raw", text: row };
    if (!/\s{2,}/.test(row)) return { kind: "raw", text: row };

    const cols = row.trim().split(/\s{2,}/).map((c) => c.trim()).filter(Boolean);
    if (cols.length < 2) return { kind: "raw", text: row };
    return { kind: "cols", cols };
  });

  const colRows = parsed.filter((r) => r.kind === "cols");
  if (colRows.length < 2) return rows;

  const counts = {};
  colRows.forEach((r) => {
    counts[r.cols.length] = (counts[r.cols.length] || 0) + 1;
  });
  const colCount = Number(
    Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 0
  );
  if (colCount < 2) return rows;

  const widths = Array(colCount).fill(0);
  colRows.forEach((r) => {
    if (r.cols.length !== colCount) return;
    r.cols.forEach((c, i) => {
      widths[i] = Math.max(widths[i], c.length);
    });
  });

  return parsed.map((r) => {
    if (r.kind === "blank") return "";
    if (r.kind === "raw") return r.text;
    if (r.cols.length !== colCount) return r.cols.join("  ");
    return r.cols.map((c, i) => c.padEnd(widths[i])).join("  ");
  });
}

export function normalizeTerminalPaste(raw) {
  if (!raw) return "";
  const lines = baseClean(raw).split("\n");
  const aligned = alignTerminalColumns(lines);
  let text = aligned.join("\n").replace(/\n{3,}/g, "\n\n").trim();

  if (!/^```/m.test(text)) {
    const lang = looksLikeCli(text) ? "powershell" : "text";
    text = `\`\`\`${lang}\n${text}\n\`\`\``;
  }
  return text;
}

/** Multi-line markdown / CLI / code dumps — preserve fences, tables, indentation. */
export function normalizeMarkdownPaste(raw) {
  if (!raw) return "";
  if (isTerminalPaste(raw)) return normalizeTerminalPaste(raw);

  const lines = baseClean(raw).split("\n");
  const out = [];
  let inFence = false;
  let fenceChar = null;

  for (const line of lines) {
    const fenceMatch = line.match(/^(`{3,}|~{3,})(.*)$/);
    if (fenceMatch) {
      const marker = fenceMatch[1];
      if (!inFence) {
        inFence = true;
        fenceChar = marker[0];
        out.push(line);
      } else if (marker[0] === fenceChar) {
        inFence = false;
        fenceChar = null;
        out.push(line);
      } else {
        out.push(line);
      }
    } else if (inFence) {
      out.push(line);
    } else {
      out.push(line.trimEnd());
    }
  }

  let text = out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
  if (!/^```/m.test(text)) {
    if (looksLikeCli(text)) {
      text = `\`\`\`powershell\n${text}\n\`\`\``;
    } else if (isCodeSnippetPaste(text)) {
      text = normalizeCodePaste(text);
    }
  }
  return text;
}

/** Unified paste gate: CLI, web LLM markdown, code snippets, plain text. */
export function smartPaste(raw) {
  if (!raw) return "";
  if (isTerminalPaste(raw)) return normalizeTerminalPaste(raw);
  const cleaned = baseClean(raw);
  if (isWebModelPaste(cleaned)) return normalizeMarkdownPaste(raw);
  if (isCodeSnippetPaste(cleaned)) return normalizeCodePaste(raw);
  if (isPasteDump(raw)) return normalizeMarkdownPaste(raw);
  return normalizeCaptureText(raw);
}

export function isPasteDump(text) {
  if (!text || typeof text !== "string") return false;
  const t = text.trim();
  if (isTerminalPaste(t)) return true;
  if (isWebModelPaste(t)) return true;
  if (isCodeSnippetPaste(t)) return true;
  if (t.includes("\n")) return true;
  if (/^```|^\|.*\|/m.test(t)) return true;
  if (/PS [A-Z]:\\|^>\s|^\$\s|npm (run|install)|yarn |pnpm /m.test(t)) return true;
  return false;
}

export function titleFromPaste(text, max = 80) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const heading = lines.find((l) => /^#{1,6}\s+/.test(l));
  if (heading) return heading.replace(/^#{1,6}\s+/, "").slice(0, max);
  const first = lines[0] || "Pegado";
  return first.replace(/^```\w*\s*/, "").slice(0, max);
}

function looksLikeCli(text) {
  return /PS [A-Z]:\\>|^PS\s|^[A-Z]:\\.*>|^>\s|^\$\s|C:\\Users\\|npm ERR!|error TS\d+/m.test(text);
}

function looksLikeCode(text) {
  if (looksLikeCli(text)) return false;
  const codeSignals = (text.match(/[{}();=]|function |const |let |class |def |import |export /g) || []).length;
  const lines = text.split("\n").filter((l) => l.trim()).length;
  return lines >= 2 && codeSignals >= 2;
}

/** Strip markdown code fence wrapper for HTML insertion. */
export function terminalPasteBody(text) {
  return String(text || "")
    .replace(/^```[\w-]*\n?/, "")
    .replace(/\n?```\s*$/, "");
}
